"use client";

import { useState } from "react";
import { ArrowRight, Download, Microscope, ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [];
function BatchGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <div key={i} className="flex flex-col space-y-6 animate-pulse bg-secondary/20 p-6 border border-primary/10">
            <div className="bg-secondary aspect-[4/5] w-full" />
            <div className="h-8 bg-secondary w-3/4" />
            <div className="h-4 bg-secondary w-1/2" />
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="text-center py-32 border border-primary/20 bg-secondary/10">
        <p className="font-mono text-sm tracking-widest uppercase text-muted-foreground">
          [ SYSTEM ERROR: NO BATCH DATA DETECTED ]
        </p>
      </div>;
  }
  return <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-px border border-primary/20">
      {posts.map((post, i) => {
      const batchImage = {
        src: post.customFields?.product_image || post.featuredImage,
        srcset: "",
        sizes: ""
      };
      return <article key={post.id} data-index={i} className="group flex flex-col bg-background p-8 transition-all duration-500 hover:bg-secondary/30 relative overflow-hidden">
            {}
            <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-100 transition-opacity">
              <span className="font-mono text-[10px] tracking-tighter text-primary">LOT #{post.id.slice(0, 6).toUpperCase()}</span>
            </div>

            <div className="relative mb-10 aspect-[4/5] overflow-hidden border border-primary/10">
              {batchImage.src && <img src={batchImage.src} data-wvc-srcset={batchImage.srcset} data-wvc-sizes={batchImage.sizes} alt={post.customFields?.product_name || post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <div className="flex gap-2">
                  <div className="p-2 bg-primary text-black rounded-lg"><ShieldCheck className="size-4" /></div>
                  <div className="p-2 bg-primary text-black rounded-lg"><Microscope className="size-4" /></div>
                </div>
              </div>
            </div>

            <div className="flex flex-col flex-grow">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary mb-3">
                {post.customFields?.badge_text || "LAB TESTED · COA VERIFIED"}
              </span>
              
              <h3 className="font-default font-bold text-2xl leading-tight text-white mb-6 group-hover:text-primary transition-colors">
                {post.customFields?.product_name || post.title}
              </h3>

              <div className="mt-auto space-y-4">
                <Separator className="bg-primary/20" />
                
                <div className="flex items-center justify-between">
                  <Link to="/ShopPage" className="font-mono text-[11px] tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                    Product Specs <ArrowRight className="size-3" />
                  </Link>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-lg border-primary/40 text-primary hover:bg-primary hover:text-black font-mono text-[10px] tracking-widest uppercase px-4">
                        View COA
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-primary/20 max-w-2xl rounded-lg">
                      <DialogHeader className="text-left">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-px w-12 bg-primary" />
                          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary">Technical Analysis</span>
                        </div>
                        <DialogTitle className="font-default font-bold text-4xl text-white mb-2">
                          {post.customFields?.product_name || post.title}
                        </DialogTitle>
                        <DialogDescription className="font-mono text-[11px] tracking-wider text-muted-foreground">
                          Click Download Full PDF Report For COA.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
                        <div className="space-y-4">
                          
                          
                        </div>
                        <div className="space-y-4">
                          
                          
                        </div>
                      </div>

                      <Separator className="bg-primary/20 mb-8" />

                      {post.customFields?.coa_pdf ? <Link to={post.customFields.coa_pdf} newTab className="w-full">
                          <Button className="w-full h-14 bg-primary text-black hover:bg-primary/90 rounded-lg font-mono text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-3">
                            <Download className="size-4" /> Download Full PDF Report
                          </Button>
                        </Link> : <div className="text-center py-4 border border-dashed border-primary/20 rounded-lg">
                          <span className="font-mono text-[10px] text-muted-foreground uppercase">PDF Report Pending Upload</span>
                        </div>}
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function BatchResults() {
  const sectionRef = useRevealOnScroll<HTMLElement>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sort, setSort] = useState("newest");
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const wp_query_params = {
    post_type: "batch",
    per_page: 9,
    orderby: sort === "newest" ? "date" : "meta_value_num",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section ref={sectionRef.ref} data-section-id="3032" id="batch-search-results" className={`bg-background text-foreground pt-32 pb-48 px-6 md:px-12 lg:px-20 transition-all duration-1000 ease-out ${sectionRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="max-w-7xl mx-auto relative">
        {}
        <div className="hidden lg:block absolute -right-16 top-0 h-full">
          <div className="sticky top-32 flex flex-col items-center gap-4">
            <span className="font-mono text-[10px] tracking-[0.5em] uppercase text-primary/30 vertical-text rotate-180" style={{
            writingMode: 'vertical-rl'
          }}>INVENTORY_INDEX_2026</span>
            <div className="w-px h-32 bg-primary/20" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-32">
          
          
          <div className="lg:col-span-4 flex flex-col justify-end">
            
          </div>
        </div>

        <div className="mb-20">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-8 border-b border-primary/20">
            <div className="flex flex-wrap gap-3">
              {categories.map((category, i) => {
              const active = selectedCategories.includes(category);
              return <Button key={category} data-index={i} variant="outline" size="sm" onClick={() => toggleCategory(category)} className={`font-mono text-[10px] tracking-widest uppercase px-6 rounded-lg transition-all duration-300 ${active ? "bg-primary text-black border-primary" : "border-primary/20 text-muted-foreground hover:border-primary hover:text-primary"}`}>
                    {category}
                  </Button>;
            })}
            </div>

            <div className="flex items-center gap-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Sort By:</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-48 bg-transparent border-primary/20 text-white font-mono text-[10px] tracking-widest uppercase rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-primary/20">
                  <SelectItem value="newest" className="font-mono text-[10px] uppercase">Newest Batches</SelectItem>
                  <SelectItem value="purity" className="font-mono text-[10px] uppercase">Highest Purity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <BatchGridConsumer />
        </PostsProvider>

        <div className="mt-32 flex flex-col items-center gap-8">
          <Separator className="w-24 bg-primary" />
          <div className="flex items-center gap-6">
            {["01", "02", "03", "04"].map((num, i) => <div key={num} data-index={i} className="flex items-center gap-6">
                <Link to="#" className={`font-mono text-lg tracking-tighter transition-colors duration-300 ${i === 0 ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                  {num}
                </Link>
                {i < 3 && <div className="size-1 bg-primary/30 rotate-45" />}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}